import {
  MatchingValidationRecordServerResponse,
  SergeeMetricResult,
  SergeeSearchResult,
  SergeeSearchScore,
  SearchStatusType,
} from '@mortee/domain/morteeAccountVerification';
import { cancelValidationRecords, getMatchingValidationRecords, saveQuickCheckValidation } from '@mortee/services/searchService';
import { AccountVerificationForm } from './searchPayeeUtils';

export interface ResultPageState {
  isApproving: boolean;
  isApproved: boolean;
  showMatchingDialog: boolean;
  matchingResults: MatchingValidationRecordServerResponse[] | null;
}

export interface ResultPageActions {
  setIsApproving: (isApproving: boolean) => void;
  setIsApproved: (isApproved: boolean) => void;
  setShowMatchingDialog: (show: boolean) => void;
  setMatchingResults: (results: MatchingValidationRecordServerResponse[] | null) => void;
}

export const handleCancelKnoxValidation = async (
  verificationResult: SergeeSearchResult | null,
  matchingResults: MatchingValidationRecordServerResponse[] | null,
  setShowMatchingDialog: (show: boolean) => void,
  onBack: (toastmessage: string, wasApproved?: boolean) => void,
): Promise<void> => {
  if (!verificationResult?.id || !matchingResults?.[0].staticId) return;
  const result = await cancelValidationRecords(verificationResult.id, matchingResults[0].staticId);
  if (result) {
    setShowMatchingDialog(false);
    onBack('Knox Validation was terminated.', true);
  }
  setShowMatchingDialog(false);
};

export const handleApprove = async (
  verificationResult: SergeeSearchResult | null,
  actions: ResultPageActions,
  onBack: (toastmessage: string, wasApproved?: boolean) => void,
): Promise<void> => {
  if (!verificationResult?.id) return;
  actions.setIsApproving(true);
  try {
    const result = await saveQuickCheckValidation(verificationResult.id);
    if (typeof result === 'string') {
      const matchingResults = await getMatchingValidationRecords(verificationResult.id);
      if (matchingResults && matchingResults.length > 0) {
        actions.setMatchingResults(matchingResults);
        actions.setShowMatchingDialog(true);
      } else {
        actions.setIsApproved(true);
        onBack('Quick check results saved.', true);
      }
    }
  } finally {
    actions.setIsApproving(false);
  }
};

export const shouldShowValidationIcon = (status?: SearchStatusType): boolean => {
  return [SearchStatusType.KnoxValidated, SearchStatusType.QuickCheckValidated].includes(
    status || SearchStatusType.QuickCheckUnknown,
  );
};

export const getValidationText = (status?: SearchStatusType, formattedDate?: string, validatingUser?: string): string => {
  const isValidated = [SearchStatusType.KnoxValidated, SearchStatusType.QuickCheckValidated].includes(
    status || SearchStatusType.QuickCheckUnknown,
  );
  const action = isValidated ? 'Accepted' : 'Checked';
  const byUser = validatingUser ? ` by ${validatingUser}` : '';

  return `${action}${byUser} on ${formattedDate}`;
};

export const shouldShowKnoxId = (knoxId?: string): boolean => {
  return !!knoxId;
};

export const shouldShowDetailsSection = (overallStatus?: SergeeSearchScore): boolean => {
  return overallStatus !== SergeeSearchScore.NotValidated;
};

export const shouldShowKnoxValidationButton = (status?: SearchStatusType): boolean => {
  return status === SearchStatusType.QuickCheckMatch;
};

export const shouldShowAcceptButton = (status?: SearchStatusType): boolean => {
  return status === SearchStatusType.QuickCheckMatch || status === SearchStatusType.QuickCheckValidated;
};

export const isAcceptButtonDisabled = (isApproving: boolean, isApproved: boolean, status?: SearchStatusType): boolean => {
  return isApproving || isApproved || status === SearchStatusType.QuickCheckValidated;
};

export const shouldShowNameChangeIndicator = (
  key: string,
  value: { result: SergeeMetricResult; value?: string },
  formData: AccountVerificationForm,
): boolean => {
  return key === 'name' && value.result === SergeeMetricResult.Match && value.value !== formData.payeeName;
};

export const getOriginalName = (formData: AccountVerificationForm): string => {
  return formData.payeeName;
};
